<script setup>
/* eslint-disable */
import { useGameStore } from '@/store/game'
import { computed, ref } from 'vue'
import { applyThemeClasses } from '@/utils/themeColors'
import { getStatGradeClass } from '@/utils/colorUtils'

const gameStore = useGameStore()

const jockeysByRegion = computed(() => {
  const grouped = {}

  gameStore.jockeys.forEach(jockey => {
    if (!grouped[jockey.region]) {
      grouped[jockey.region] = []
    }

    grouped[jockey.region].push(jockey)
  })

  // Sort jockeys by skill grade
  for (const region in grouped) {
    grouped[region].sort((a, b) => {
      const skillGradeMap = {
        'S': 6, 'A': 5, 'B': 4, 'C': 3, 'D': 2, 'E': 1, 'F': 0
      };
      return skillGradeMap[b.skillGrade] - skillGradeMap[a.skillGrade];
    });
  }

  return grouped;
});

const expandedRegions = ref([]);

const toggleRegion = (region) => {
  const index = expandedRegions.value.indexOf(region);
  if (index === -1) {
    expandedRegions.value.push(region);
  } else {
    expandedRegions.value.splice(index, 1);
  }
};

const getRegionDisplayName = (region) => {
  const regions = {
    'region1': 'Eastern Metropolis',
    'region2': 'Junon Area',
    'region3': 'Western Seaboard',
    'region4': 'Central Range',
    'region5': 'Mideel Forest',
    'region6': 'Northern Territory'
  };
  return regions[region] || region;
};

</script>

<template>
  <div class="px-4 py-6 sm:px-0">
    <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} p-6 mb-6`">
      <div class="flex justify-between items-center">
        <h1 :class="applyThemeClasses('text.primary')">All Jockeys</h1>
        
          <span :class="applyThemeClasses('text.primaryLow')">Total Jockeys: {{ gameStore.jockeys.length }}</span>
        
      </div>
    </div>

    <div class="space-y-8">
      <div v-for="(jockeys, region) in jockeysByRegion" :key="region" :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')}`">
        <button
          @click="toggleRegion(region)"
          class="w-full p-4 border-b flex justify-between items-center"
        >
          <div>
            <h2 :class="applyThemeClasses('text.primary')">{{ getRegionDisplayName(region) }}</h2>
            <p :class="applyThemeClasses('text.secondary')">{{ jockeys.length }} jockeys</p>
          </div>
          <svg
            class="w-5 h-5 text-gray-500 transform transition-transform duration-200"
            :class="{ 'rotate-180': expandedRegions.includes(region) }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div v-if="expandedRegions.includes(region)" class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="jockey in jockeys" :key="jockey.id" class="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
              <div class="flex">
                <!-- Jockey Info -->
                <div class="flex-1 p-4">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-lg bg-gray-200"></div>
                    <div>
                      <h3 :class="applyThemeClasses('text.secondaryBold')">{{ jockey.name }}</h3>
                      <p :class="applyThemeClasses('text.secondary')">
                        {{ getRegionDisplayName(jockey.region) }}
                      </p>
                    </div>
                  </div>

                  <div class="mt-2 flex items-center gap-2">
                    <span
                      :class="[
                        getStatGradeClass(jockey.skillGrade),
                        'px-2 py-1 rounded text-sm font-medium'
                      ]"
                    >
                      {{ jockey.skillGrade }}
                    </span>
                    <div class="text-sm text-gray-600">
                      <span :class="applyThemeClasses('text.secondary')">{{ jockey.weightCategory }}</span>
                    </div>
                  </div>

                  <!-- Recent Form -->
                  <div class="mt-4 space-y-2">
                    <span :class="applyThemeClasses('text.secondaryBold')">Recent Form:</span>
                    <div class="flex gap-1">
                      <span
                        v-for="(result, index) in jockey.recentForm"
                        :key="index"
                        :class="[
                          'w-5 h-5 flex items-center justify-center rounded text-xs font-medium',
                          result === null
                            ? 'bg-gray-100 text-gray-600'
                            : result === 1
                              ? 'bg-yellow-100 text-yellow-800'
                              : result === 2
                                ? 'bg-gray-200 text-gray-800'
                                : result === 3
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-red-50 text-red-800'
                        ]"
                      >
                        {{ result === null ? '-' : result }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>